import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ClaimChips } from '../../../../../../shared/sso-workspace/claim-chips'
import { RoleMappingPanel } from '../../../../../../shared/sso-workspace/role-mapping-panel'
import { scrollBehavior } from '../../../../../../shared/sso-workspace/section-nav'
import { ConfirmDialog } from '../../../../../../shared/sso-workspace/confirm-dialog'
import { serverMessage } from '../../../../../../shared/sso-workspace/format'
import { FleetGrid, useGridList } from '../../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../../shared/table-builder/table-ag-client'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../../../shared/table-builder/table-columns'
import { Menu, MenuDelete } from '../../../../../../shared/menu/menu'
import { IconRole } from '../../../../../../shared/icon/icon'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'

const HIGHLIGHT_MS = 1800

// What the identity provider tells us (the claim chips) and what EducationXR
// does with it (the role mappings). Mappings save through their own
// endpoint, so they are never part of the form's unsaved count. Delete
// (audit F4: it needed the legacy /mapping page, one click, no confirm)
// asks first and says what stops happening.
export const SectionClaims = ({ claimsSupported, mappings, links, providerID, canEdit, onMappingsChanged, onShowRule, onDeleteMapping }) => {
  const [panel, setPanel] = useState(null) // claim string while open
  const [highlight, setHighlight] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [deleteError, deleteFailure, setDeleteError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('')
  const timer = useRef(null)

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const handleDelete = async () => {
    setBusy(true)
    setDeleteError(null)
    try {
      await onDeleteMapping(deleting.id)
    } catch (err) {
      setDeleteError(serverMessage(err, "The role mapping couldn't be deleted."), err)
      setBusy(false)
      return
    }
    setBusy(false)
    setStatus(`Deleted the mapping from ${deleting.claimName} = ${deleting.claimValue}.`)
    setDeleting(null)
    onMappingsChanged && onMappingsChanged()
  }

  const canDelete = canEdit && typeof onDeleteMapping === 'function'

  // The grid's api, from its ready handler: a new highlight re-draws the
  // rows (AG Grid reads getRowClass as it draws a row) and scrolls the first
  // matching one into view.
  const gridApi = useRef(null)
  const highlightRef = useRef(null)
  const tableRef = useRef()
  const gridBox = useRef(null)
  const gridList = useGridList(mappings)
  const handleReady = agGrid => { gridApi.current = agGrid && agGrid.api }
  const rowClass = useCallback(params => (params.data && highlightRef.current && params.data.claimName === highlightRef.current ? 'sw-hl' : ''), [])

  useEffect(() => {
    highlightRef.current = highlight
    const api = gridApi.current
    if (api && !api.isDestroyed()) api.redrawRows()
  }, [highlight])

  const showMappings = claim => {
    setHighlight(claim)
    const box = gridBox.current
    if (box && box.scrollIntoView) box.scrollIntoView({ behavior: scrollBehavior(), block: 'center' })
    const index = (mappings || []).findIndex(m => m.claimName === claim)
    const api = gridApi.current
    if (api && !api.isDestroyed() && index >= 0) {
      // The row's place in the grid's current (sorted, filtered) order.
      let displayed = -1
      api.forEachNodeAfterFilterAndSort((node, i) => { if (displayed < 0 && node.data && node.data.claimName === claim) displayed = i })
      if (displayed >= 0) api.ensureIndexVisible(displayed, 'middle')
    }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setHighlight(null), HIGHLIGHT_MS)
  }

  return (
    <div className="d-grid gap-3">
      <ClaimChips claimsSupported={claimsSupported || []} mappings={mappings || []} links={links || []} canEdit={canEdit}
        onShowMappings={showMappings} onShowRule={onShowRule} onAddMapping={claim => setPanel(claim)} />
      <div className="sw-subh">
        <h3>Role mappings</h3>
        <p className="sw-status" role="status">{status}</p>
        {canEdit && <button type="button" className="btn btn-sm sw-btn" onClick={() => setPanel('')}>Add role mapping</button>}
      </div>
      {mappings === undefined && <p className="sw-hint m-0">Loading role mappings…</p>}
      {mappings === null && <p className="sw-hint m-0">Role mappings couldn't be loaded.</p>}
      {mappings &&
        <div ref={gridBox}>
          <FleetGrid tableRef={tableRef} exportName="role-mappings" compact flush title="role mappings">
            <TableAgClient
              ref={tableRef}
              queryKey="mappings"
              apiList={gridList}
              onReady={handleReady}
              getRowClass={rowClass}
              emptyState={{ Icon: IconRole, title: 'No role mappings yet', message: 'Add one to give people a role when their identity provider sends a claim value.' }}>
              <ColumnFormat field="claimName" header="Claim" render={codeCell} sortable />
              <ColumnFormat field="claimValue" header="Value" render={codeCell} sortable />
              <ColumnText field="organizationName" header="Organization" sortable />
              <ColumnText field="roleName" header="Role" sortable />
              {canDelete &&
                <ColumnMenu
                  menu={m =>
                    <Menu small>
                      <MenuDelete show onClick={() => { setDeleteError(null); setDeleting(m) }} />
                    </Menu>}
                />}
            </TableAgClient>
          </FleetGrid>
        </div>}
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete this role mapping?"
        body={deleting ? `People whose ${deleting.claimName} is ${deleting.claimValue} stop being given ${deleting.roleName || 'this role'} in ${deleting.organizationName || 'the organization'} when they sign in. Roles they already have stay, since mappings only ever grant.` : ''}
        confirmText="Delete mapping"
        busy={busy}
        error={deleteError}
        failure={deleteFailure}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
      {/* The panel is portalled out of the page's <form>: inside it, Enter in
          one of its inputs would submit (and save) the whole connection. */}
      {createPortal(
        <div onSubmit={e => e.stopPropagation()}>
          <RoleMappingPanel open={panel !== null} claim={panel || ''} providerID={providerID} organizations={links || []}
            onClose={() => setPanel(null)} onAdded={() => { setPanel(null); onMappingsChanged && onMappingsChanged() }} />
        </div>,
        document.body
      )}
    </div>
  )
}

const codeCell = row => (row.value ? <code>{row.value}</code> : '')
